import { create } from 'zustand';

export const NATIVE_SLIDE_PENDING_MS = 8000;

export type LiveSequenceStepTiming = {
  index: number;
  enteredAt: number;
  holdUntil?: number;
  videoStartedAt: Record<string, number>;
};

type LiveSequenceIndexState = {
  indexByItemId: Record<string, number>;
  totalPagesByItemId: Record<string, number>;
  /** Wall-clock ms when the operator last set this item locally (optimistic). */
  optimisticTouchedAtByItemId: Record<string, number>;
  /** Wall-clock ms when the operator sent native Next/Prev (badge pulses until confirm). */
  nativePendingAtByItemId: Record<string, number>;
  /** Server time minus local time, from the last play-state or sequence event. */
  serverOffsetMs: number;
  /** Authoritative step window for the live preview bar. */
  stepTimingByItemId: Record<string, LiveSequenceStepTiming>;
  /**
   * Bumps when Control leaves or the player socket drops. Step timing from an older
   * generation is not the playout on air now.
   */
  playoutGeneration: number;
  stepTimingGenerationByItemId: Record<string, number>;
  setIndex: (itemId: string, page: number, options?: { optimistic?: boolean; confirm?: boolean }) => void;
  setServerClock: (emittedAtMs: number) => void;
  setStepTiming: (itemId: string, timing: LiveSequenceStepTiming) => void;
  clearStepTiming: (itemId: string) => void;
  /** True when this item's step window was published by the current playout generation. */
  isActiveStepTiming: (itemId: string) => boolean;
  /** Drop step windows. A later GET or sequence event may publish the slide again. */
  invalidatePlayoutTiming: () => void;
  reportTotalPages: (itemId: string, totalPages: number) => void;
  markNativePending: (itemId: string) => void;
  clearNativePending: (itemId: string) => void;
  /** True when a local slide pick should win over a stale entity GET poll. */
  isRecentlyOptimistic: (itemId: string, withinMs?: number) => boolean;
  isNativePending: (itemId: string, withinMs?: number) => boolean;
  reset: () => void;
};

const initialState = {
  indexByItemId: {} as Record<string, number>,
  totalPagesByItemId: {} as Record<string, number>,
  optimisticTouchedAtByItemId: {} as Record<string, number>,
  nativePendingAtByItemId: {} as Record<string, number>,
  serverOffsetMs: 0,
  stepTimingByItemId: {} as Record<string, LiveSequenceStepTiming>,
  playoutGeneration: 0,
  stepTimingGenerationByItemId: {} as Record<string, number>,
};

function withoutItem<T>(record: Record<string, T>, itemId: string): Record<string, T> {
  if (!(itemId in record)) {
    return record;
  }
  const next = { ...record };
  delete next[itemId];
  return next;
}

export const useLiveSequenceIndexStore = create<LiveSequenceIndexState>((set, get) => ({
  ...initialState,
  setServerClock: (emittedAtMs) => {
    if (!(emittedAtMs > 0)) return;
    set({ serverOffsetMs: emittedAtMs - Date.now() });
  },
  setStepTiming: (itemId, timing) => {
    const state = get();
    set({
      stepTimingByItemId: { ...state.stepTimingByItemId, [itemId]: timing },
      stepTimingGenerationByItemId: { ...state.stepTimingGenerationByItemId, [itemId]: state.playoutGeneration },
    });
  },
  clearStepTiming: (itemId) => {
    const state = get();
    if (!(itemId in state.stepTimingByItemId)) return;
    set({
      stepTimingByItemId: withoutItem(state.stepTimingByItemId, itemId),
      stepTimingGenerationByItemId: withoutItem(state.stepTimingGenerationByItemId, itemId),
    });
  },
  isActiveStepTiming: (itemId) => {
    const state = get();
    const timing = state.stepTimingByItemId[itemId];
    return timing != null && state.stepTimingGenerationByItemId[itemId] === state.playoutGeneration;
  },
  invalidatePlayoutTiming: () => {
    const state = get();
    set({
      playoutGeneration: state.playoutGeneration + 1,
      stepTimingByItemId: {},
      stepTimingGenerationByItemId: {},
    });
  },
  setIndex: (itemId, page, options) => {
    const state = get();
    const max = state.totalPagesByItemId[itemId];
    const next = Math.max(1, page);
    const clearPending = Boolean(options?.confirm || options?.optimistic);
    const clearOptimistic = Boolean(options?.confirm);
    if (state.indexByItemId[itemId] === next && (max == null || next <= max) && !options?.optimistic) {
      if (
        (clearPending && itemId in state.nativePendingAtByItemId) ||
        (clearOptimistic && itemId in state.optimisticTouchedAtByItemId)
      ) {
        set({
          ...(clearPending ? { nativePendingAtByItemId: withoutItem(state.nativePendingAtByItemId, itemId) } : {}),
          ...(clearOptimistic
            ? { optimisticTouchedAtByItemId: withoutItem(state.optimisticTouchedAtByItemId, itemId) }
            : {}),
        });
      }
      return;
    }
    if (max != null && next > max) {
      // Authoritative DB/WS/layer seed (or operator jump) above a stale smaller total —
      // e.g. play-once deck's 6 stamped onto a 64-slide Next resume item. Raise total; never clamp down.
      set({
        indexByItemId: { ...state.indexByItemId, [itemId]: next },
        totalPagesByItemId: { ...state.totalPagesByItemId, [itemId]: next },
        ...(options?.optimistic
          ? {
              optimisticTouchedAtByItemId: {
                ...state.optimisticTouchedAtByItemId,
                [itemId]: Date.now(),
              },
            }
          : {}),
        ...(clearPending ? { nativePendingAtByItemId: withoutItem(state.nativePendingAtByItemId, itemId) } : {}),
        ...(clearOptimistic
          ? { optimisticTouchedAtByItemId: withoutItem(state.optimisticTouchedAtByItemId, itemId) }
          : {}),
      });
      return;
    }
    set({
      indexByItemId: { ...state.indexByItemId, [itemId]: next },
      ...(options?.optimistic
        ? {
            optimisticTouchedAtByItemId: {
              ...state.optimisticTouchedAtByItemId,
              [itemId]: Date.now(),
            },
          }
        : {}),
      ...(clearPending ? { nativePendingAtByItemId: withoutItem(state.nativePendingAtByItemId, itemId) } : {}),
      ...(clearOptimistic
        ? { optimisticTouchedAtByItemId: withoutItem(state.optimisticTouchedAtByItemId, itemId) }
        : {}),
    });
  },
  reportTotalPages: (itemId, totalPages) => {
    const state = get();
    if (!(totalPages > 0)) {
      return;
    }
    if (state.totalPagesByItemId[itemId] === totalPages) {
      return;
    }
    const current = state.indexByItemId[itemId];
    const existingTotal = state.totalPagesByItemId[itemId];
    // Never shrink a known larger total / higher resume index — cross-deck contamination often
    // reports the on-air play-once length (e.g. 6) onto the Next cue item (64).
    if (existingTotal != null && totalPages < existingTotal) {
      return;
    }
    if (current != null && current > totalPages) {
      return;
    }
    set({
      totalPagesByItemId: { ...state.totalPagesByItemId, [itemId]: totalPages },
    });
  },
  markNativePending: (itemId) => {
    const state = get();
    set({
      nativePendingAtByItemId: { ...state.nativePendingAtByItemId, [itemId]: Date.now() },
      optimisticTouchedAtByItemId: withoutItem(state.optimisticTouchedAtByItemId, itemId),
    });
  },
  clearNativePending: (itemId) => {
    const state = get();
    if (!(itemId in state.nativePendingAtByItemId)) {
      return;
    }
    set({ nativePendingAtByItemId: withoutItem(state.nativePendingAtByItemId, itemId) });
  },
  // Long enough to cover cold agent open / insert transition without badge flicker.
  isRecentlyOptimistic: (itemId, withinMs = 5000) => {
    const touchedAt = get().optimisticTouchedAtByItemId[itemId];
    if (touchedAt == null) {
      return false;
    }
    return Date.now() - touchedAt < withinMs;
  },
  isNativePending: (itemId, withinMs = NATIVE_SLIDE_PENDING_MS) => {
    const pendingAt = get().nativePendingAtByItemId[itemId];
    if (pendingAt == null) {
      return false;
    }
    return Date.now() - pendingAt < withinMs;
  },
  reset: () => set(initialState),
}));
